import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, ArrowRight, Anchor, Check } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { IMAGES } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIXED_AMOUNTS = [300, 500, 1000, 1500];
const EXPERIENCE_GIFTS = [
  { id: "Daytime Cruise (3 hours)", price: 800 },
  { id: "Sunset Sail (2 hours)", price: 550 },
  { id: "Overnight Stay", price: 1500 },
  { id: "Full Day Charter (6 hours)", price: 1550 },
];

export default function GiftPage() {
  const [voucherType, setVoucherType] = useState("amount"); // amount | experience
  const [amount, setAmount] = useState(500);
  const [customAmount, setCustomAmount] = useState("");
  const [experience, setExperience] = useState(EXPERIENCE_GIFTS[0].id);
  const [form, setForm] = useState({
    purchaser_name: "",
    purchaser_email: "",
    recipient_name: "",
    recipient_email: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const finalAmount = (() => {
    if (voucherType === "experience") return EXPERIENCE_GIFTS.find((e) => e.id === experience)?.price || 0;
    if (customAmount) return Number(customAmount);
    return amount;
  })();

  const canSubmit =
    form.purchaser_name &&
    form.purchaser_email &&
    form.recipient_name &&
    form.recipient_email &&
    finalAmount >= 50 &&
    finalAmount <= 5000;

  async function purchase() {
    if (!canSubmit) return;
    toast.info("Online checkout is coming soon. Please email us at anchormgtbda@gmail.com with your gift details and we'll send wire instructions and a personal certificate.");
  }

  return (
    <div data-testid="gift-page" className="bg-sand">
      {/* Hero */}
      <section className="relative min-h-[60vh] overflow-hidden">
        <img src={IMAGES.sunset} alt="Aboard the Bavaria 35" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--am-navy)]/40 via-[color:var(--am-navy)]/55 to-[color:var(--am-navy)]/85" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-32 pb-20 text-white">
          <div className="overline text-white/80 mb-5">A Charter, Wrapped</div>
          <h1 className="font-serif-display font-light text-5xl md:text-6xl lg:text-7xl leading-[1.02] max-w-3xl">
            Give the gift of <em className="italic">a slow afternoon at sea.</em>
          </h1>
          <p className="mt-6 text-white/85 max-w-xl leading-relaxed">
            Send a private sailing experience aboard our 35ft Bavaria. Beautifully designed certificate, instant email delivery, valid one full year.
          </p>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 lg:px-12 -mt-20 relative z-10 mb-24">
        <div className="bg-white border border-[color:var(--am-line)] p-8 lg:p-12 grid lg:grid-cols-12 gap-10">
          {/* Left: Configurator */}
          <div className="lg:col-span-7 space-y-10">
            {/* Type toggle */}
            <div>
              <div className="overline mb-3">Gift Type</div>
              <div className="flex border border-[color:var(--am-line)]">
                <button
                  type="button"
                  onClick={() => setVoucherType("amount")}
                  className={`flex-1 py-3 text-xs tracking-[0.22em] uppercase transition-colors ${
                    voucherType === "amount" ? "bg-navy text-white" : "text-navy hover:bg-pink-soft"
                  }`}
                  data-testid="gift-type-amount"
                >
                  Monetary Amount
                </button>
                <button
                  type="button"
                  onClick={() => setVoucherType("experience")}
                  className={`flex-1 py-3 text-xs tracking-[0.22em] uppercase transition-colors ${
                    voucherType === "experience" ? "bg-navy text-white" : "text-navy hover:bg-pink-soft"
                  }`}
                  data-testid="gift-type-experience"
                >
                  Specific Experience
                </button>
              </div>
            </div>

            {/* Amount selector */}
            {voucherType === "amount" && (
              <div>
                <div className="overline mb-3">Choose an amount</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                  {FIXED_AMOUNTS.map((a) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => {
                        setAmount(a);
                        setCustomAmount("");
                      }}
                      className={`py-4 border transition-colors ${
                        amount === a && !customAmount
                          ? "border-navy bg-navy text-white"
                          : "border-[color:var(--am-line)] text-navy hover:border-navy"
                      }`}
                      data-testid={`gift-amount-${a}`}
                    >
                      <div className="font-serif-display text-2xl">${a}</div>
                    </button>
                  ))}
                </div>
                <Label htmlFor="custom" className="overline block mb-2">Or custom amount ($50–$5,000)</Label>
                <Input
                  id="custom"
                  type="number"
                  min={50}
                  max={5000}
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="Enter custom amount"
                  className="rounded-none"
                  data-testid="gift-custom-amount"
                />
              </div>
            )}

            {/* Experience selector */}
            {voucherType === "experience" && (
              <div>
                <div className="overline mb-3">Choose an experience</div>
                <div className="space-y-2">
                  {EXPERIENCE_GIFTS.map((e) => (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => setExperience(e.id)}
                      className={`w-full text-left p-5 border flex items-center justify-between transition-colors ${
                        experience === e.id
                          ? "border-navy bg-white"
                          : "border-[color:var(--am-line)] hover:border-navy/50"
                      }`}
                      data-testid={`gift-exp-${e.id}`}
                    >
                      <div className="font-serif-display text-xl text-navy">{e.id}</div>
                      <div className="font-serif-display text-xl text-navy">${e.price}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Form */}
            <div className="space-y-5 pt-4 border-t border-[color:var(--am-line)]">
              <div className="overline">Your Details</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="overline block mb-2">Your name</Label>
                  <Input
                    value={form.purchaser_name}
                    onChange={(e) => setForm({ ...form, purchaser_name: e.target.value })}
                    className="rounded-none"
                    data-testid="gift-purchaser-name"
                  />
                </div>
                <div>
                  <Label className="overline block mb-2">Your email</Label>
                  <Input
                    type="email"
                    value={form.purchaser_email}
                    onChange={(e) => setForm({ ...form, purchaser_email: e.target.value })}
                    className="rounded-none"
                    data-testid="gift-purchaser-email"
                  />
                </div>
              </div>
              <div className="overline pt-4">Recipient</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="overline block mb-2">Their name</Label>
                  <Input
                    value={form.recipient_name}
                    onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                    className="rounded-none"
                    data-testid="gift-recipient-name"
                  />
                </div>
                <div>
                  <Label className="overline block mb-2">Their email</Label>
                  <Input
                    type="email"
                    value={form.recipient_email}
                    onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
                    className="rounded-none"
                    data-testid="gift-recipient-email"
                  />
                </div>
              </div>
              <div>
                <Label className="overline block mb-2">A short note (optional)</Label>
                <Textarea
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value.slice(0, 280) })}
                  placeholder="Happy birthday, Dad — pour a strong one and watch the sun fall."
                  className="rounded-none"
                  data-testid="gift-message"
                />
                <div className="text-xs text-[color:var(--am-muted)] mt-1">{form.message.length}/280</div>
              </div>
            </div>
          </div>

          {/* Right: Summary */}
          <aside className="lg:col-span-5">
            <div className="sticky top-28 bg-pink-soft p-8">
              <Gift size={22} strokeWidth={1.4} className="text-navy mb-4" />
              <div className="overline mb-2">The Gift</div>
              <div className="font-serif-display text-5xl text-navy leading-tight mb-2">
                {voucherType === "experience"
                  ? experience.split(" (")[0]
                  : `$${finalAmount.toLocaleString()}`}
              </div>
              {voucherType === "experience" && (
                <div className="text-sm text-navy/70 italic mb-3">{experience.split(" (")[1]?.replace(")", "")}</div>
              )}
              <div className="hairline my-6 bg-navy/10" />
              <ul className="space-y-3 text-sm text-navy/80">
                <li className="flex gap-2"><Check size={14} className="mt-0.5 flex-shrink-0" /> Branded PDF certificate emailed instantly</li>
                <li className="flex gap-2"><Check size={14} className="mt-0.5 flex-shrink-0" /> Unique redemption code, applied at checkout</li>
                <li className="flex gap-2"><Check size={14} className="mt-0.5 flex-shrink-0" /> Valid for 1 full year from purchase</li>
                <li className="flex gap-2"><Check size={14} className="mt-0.5 flex-shrink-0" /> Transferable, never lose value if unused for a single charter</li>
              </ul>
              <div className="mt-8 pt-6 border-t border-navy/15">
                <div className="overline mb-2">Total</div>
                <div className="font-serif-display text-4xl text-navy" data-testid="gift-total">
                  ${finalAmount.toLocaleString()}
                </div>
              </div>
              <button
                onClick={purchase}
                disabled={!canSubmit}
                className="btn-primary w-full justify-center mt-8"
                data-testid="gift-purchase-button"
              >
                Request Gift Voucher <ArrowRight size={14} />
              </button>
              <div className="text-xs text-[color:var(--am-muted)] mt-4 text-center">
                Online checkout coming soon · We'll email you wire instructions
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 lg:px-12 pb-24 grid md:grid-cols-3 gap-8">
        {[
          { n: "01", t: "Choose", d: "Pick a monetary amount or a specific experience to gift." },
          { n: "02", t: "Request", d: "Submit your gift details — we'll email you bank wire instructions to complete the purchase." },
          { n: "03", t: "Delivered", d: "Recipient gets a branded PDF certificate and unique code by email immediately." },
        ].map((s) => (
          <div key={s.n} className="bg-white border border-[color:var(--am-line)] p-8">
            <div className="overline mb-3">{s.n}</div>
            <div className="font-serif-display text-2xl text-navy mb-2 flex items-center gap-2"><Anchor size={16} strokeWidth={1.4} /> {s.t}</div>
            <p className="text-sm text-[color:var(--am-muted)] leading-relaxed">{s.d}</p>
          </div>
        ))}
      </section>
    </div>
  );
}
